import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { AppUser, Task, Workstream } from '../../pm/types';
import { DEFAULT_WORKSTREAM_COLOR, STATUS_LABELS, displayName } from '../../pm/constants';
// formatDueDate dropped here — its only consumer was the diamond marker
// tooltip, which the design removed.
import { formatMonthDay, workstreamDateRange } from '../../pm/dueDate';

interface Props {
  tasks: Task[];                  // pre-filtered task set
  workstreams: Workstream[];      // one Gantt row per workstream (in display order)
  admins: AppUser[];
  projectId: string;
}

const DAY = 86400000;
const PAD_DAYS = 5; // padding on each side of the global range

// Pick a tick cadence that produces a readable axis for the given span.
function chooseTickCadence(spanDays: number): { stepDays: number; format: 'day' | 'month' } {
  if (spanDays <= 21)  return { stepDays: 1,   format: 'day'   };
  if (spanDays <= 90)  return { stepDays: 7,   format: 'day'   };
  if (spanDays <= 365) return { stepDays: 30,  format: 'month' };
  return                       { stepDays: 90, format: 'month' };
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const GanttView: React.FC<Props> = ({ tasks, workstreams, admins, projectId }) => {
  const history = useHistory();

  // Tasks grouped by workstream id.
  const tasksByWs = useMemo(() => {
    const m: Record<string, Task[]> = {};
    for (const t of tasks) {
      if (!m[t.workstreamId]) m[t.workstreamId] = [];
      m[t.workstreamId].push(t);
    }
    return m;
  }, [tasks]);

  // Project-level min/max dueDate. If nothing is dated, the timeline is empty.
  const globalRange = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const t of tasks) {
      if (typeof t.dueDate !== 'number') continue;
      if (t.dueDate < min) min = t.dueDate;
      if (t.dueDate > max) max = t.dueDate;
    }
    if (min === Infinity) return null;
    // Pad both ends so markers aren't flush against the axis edges. Also
    // handle the single-day case where min === max.
    const pad = PAD_DAYS * DAY;
    if (min === max) return { min: min - pad, max: max + pad };
    return { min: min - pad, max: max + pad };
  }, [tasks]);

  const axisTicks = useMemo(() => {
    if (!globalRange) return [];
    const spanDays = Math.max(1, Math.round((globalRange.max - globalRange.min) / DAY));
    const { stepDays, format } = chooseTickCadence(spanDays);
    const stepMs = stepDays * DAY;
    const ticks: { ms: number; label: string }[] = [];
    // Snap the first tick to a clean step boundary (start-of-day in local).
    let cursor = startOfDay(globalRange.min);
    while (cursor < globalRange.min) cursor += stepMs;
    while (cursor <= globalRange.max) {
      const d = new Date(cursor);
      const label = format === 'day'
        ? formatMonthDay(cursor)
        : d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
      ticks.push({ ms: cursor, label });
      cursor += stepMs;
    }
    return ticks;
  }, [globalRange]);

  const undatedTasks = useMemo(() => tasks.filter((t) => typeof t.dueDate !== 'number'), [tasks]);

  // Today marker — only rendered when it falls inside the timeline.
  const nowMs = Date.now();
  const showTodayLine = !!globalRange && nowMs >= globalRange.min && nowMs <= globalRange.max;

  function pct(ms: number): number {
    if (!globalRange) return 0;
    const span = globalRange.max - globalRange.min;
    if (span <= 0) return 0;
    return ((ms - globalRange.min) / span) * 100;
  }

  function ownerName(uid: string): string {
    if (!uid) return 'Unassigned';
    const a = admins.find((u) => u.uid === uid);
    return displayName(a?.email) || 'Unassigned';
  }

  function openTask(t: Task) {
    history.replace({
      pathname: `/Projects/${projectId}`,
      hash: `#task-${t.id}`,
    });
  }

  if (!globalRange) {
    return (
      <div className="pm-gantt-empty">
        <h3>No dated tasks yet</h3>
        <p>Add due dates to tasks (List or Kanban view) and they'll show up on the timeline here.</p>
      </div>
    );
  }

  const todayLabel = new Date(nowMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <div className="pm-gantt">
      {/* Two-column canvas matching the design's project ProjectGantt:
          • Left column: workstream labels (sticky, fixed width)
          • Right column: 40px axis row + per-workstream tracks with bars,
            today line + pill, and task markers anchored along the axis.
          Heights of the axis row + workstream rows are kept in sync so the
          left labels line up perfectly with the right tracks. */}
      <div className="pm-gantt-canvas">
        <div className="pm-gantt-col">
          <div className="pm-gantt-col-axis" aria-hidden="true" />
          {workstreams.map((ws) => {
            const wsTasks = tasksByWs[ws.id] || [];
            const color = ws.color || DEFAULT_WORKSTREAM_COLOR;
            return (
              <div key={ws.id} className="pm-gantt-col-row">
                <span className="pm-gantt-col-dot" style={{ background: color }} />
                <span className="pm-gantt-col-name" title={ws.title}>{ws.title}</span>
                <span className="pm-gantt-col-count">{wsTasks.length}</span>
              </div>
            );
          })}
        </div>

        <div className="pm-gantt-timeline">
          <div className="pm-gantt-timeline-inner">
            <div className="pm-gantt-scale">
              {axisTicks.map((tick, idx) => (
                <div
                  key={tick.ms}
                  className={`pm-gantt-tick${idx === 0 || tick.label.match(/[A-Za-z]/) ? ' is-month' : ''}`}
                  style={{ left: `${pct(tick.ms)}%` }}
                >
                  {tick.label}
                </div>
              ))}
            </div>

            {showTodayLine ? (
              <div className="pm-gantt-today" style={{ left: `${pct(nowMs)}%` }}>
                <span className="pm-gantt-today-pill">Today · {todayLabel}</span>
              </div>
            ) : null}

            {workstreams.map((ws) => {
              const wsTasks = tasksByWs[ws.id] || [];
              const range = workstreamDateRange(wsTasks);
              const color = ws.color || DEFAULT_WORKSTREAM_COLOR;
              const done = wsTasks.filter((t) => t.status === 'done').length;
              const total = wsTasks.length;
              const progress = total > 0 ? done / total : 0;
              return (
                <div key={ws.id} className="pm-gantt-track">
                  {range ? (
                    <div
                      className="pm-gantt-bar-bg"
                      style={{
                        left: `${pct(range.min)}%`,
                        width: `${Math.max(2, pct(range.max) - pct(range.min))}%`,
                        background: color + '22',
                        borderColor: color + '66',
                      }}
                      title={`${formatMonthDay(range.min)} – ${formatMonthDay(range.max)}`}
                    >
                      <div
                        className="pm-gantt-bar-fill"
                        style={{
                          width: `${Math.max(progress * 100, total === 0 ? 0 : 6)}%`,
                          background: color,
                        }}
                      />
                      {total > 0 ? (
                        <span
                          className="pm-gantt-bar-label"
                          style={{ color: progress > 0.18 ? '#fff' : color }}
                        >
                          {done}/{total}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {/* Task-level diamond markers removed per design — the
                   * progress fill inside the bar carries the same signal
                   * without cluttering the timeline. Clicking the bar opens
                   * the workstream below; deep-linking to a specific task
                   * still works from the List view's task rows. */}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {undatedTasks.length > 0 ? (
        <div className="pm-gantt-undated">
          <div className="pm-gantt-undated-header">Unscheduled ({undatedTasks.length})</div>
          <ul className="pm-gantt-undated-list">
            {undatedTasks.map((t) => {
              const ws = workstreams.find((w) => w.id === t.workstreamId);
              const color = ws?.color || DEFAULT_WORKSTREAM_COLOR;
              return (
                <li key={t.id}>
                  <span className="pm-gantt-undated-dot" style={{ background: color }} />
                  <button type="button" className="pm-link-button" onClick={() => openTask(t)}>
                    {t.label}
                  </button>
                  <span className="pm-gantt-undated-meta">
                    {STATUS_LABELS[t.status]} · {ownerName(t.ownerId || '')}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default GanttView;
